'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';

export type SupplierRoleType =
  | 'Owner_Admin' |'Sales_Manager' |'Sales_Rep' |'Warehouse_Manager' |'Driver' |'Accountant';

export interface StaffMember {
  roleId: number;
  supplierId: number;
  staffName: string;
  roleType: SupplierRoleType;
  pinCode: string;
  phoneWhatsapp: string | null;
  isActive: boolean;
  permissions: Record<string, boolean>;
}

interface RoleContextType {
  activeStaff: StaffMember | null;
  isRoleAuthenticated: boolean;
  loginWithPin: (pin: string, supplierId?: number) => Promise<boolean>;
  logoutRole: () => void;
  hasPermission: (key: string) => boolean;
  getRoleLabel: (role: SupplierRoleType) => string;
  getRoleColor: (role: SupplierRoleType) => string;
  getAllowedRoutes: (role: SupplierRoleType) => string[];
}

const RoleContext = createContext<RoleContextType>({
  activeStaff: null,
  isRoleAuthenticated: false,
  loginWithPin: async () => false,
  logoutRole: () => {},
  hasPermission: () => false,
  getRoleLabel: () => '',
  getRoleColor: () => '',
  getAllowedRoutes: () => [],
});

export const ROLE_LABELS: Record<SupplierRoleType, { ar: string; fr: string }> = {
  Owner_Admin:       { ar: 'المالك / المدير', fr: 'Propriétaire / Admin' },
  Sales_Manager:     { ar: 'مدير المبيعات',   fr: 'Directeur Commercial' },
  Sales_Rep:         { ar: 'مندوب المبيعات',  fr: 'Représentant Commercial' },
  Warehouse_Manager: { ar: 'مسؤول المخزن',    fr: 'Responsable Entrepôt' },
  Driver:            { ar: 'السائق',           fr: 'Chauffeur-Livreur' },
  Accountant:        { ar: 'المحاسب',          fr: 'Comptable' },
};

export const ROLE_COLORS: Record<SupplierRoleType, string> = {
  Owner_Admin:       'bg-purple-100 text-purple-700 border-purple-200',
  Sales_Manager:     'bg-blue-100 text-blue-700 border-blue-200',
  Sales_Rep:         'bg-sky-100 text-sky-700 border-sky-200',
  Warehouse_Manager: 'bg-orange-100 text-orange-700 border-orange-200',
  Driver:            'bg-green-100 text-green-700 border-green-200',
  Accountant:        'bg-rose-100 text-rose-700 border-rose-200',
};

// Routes each role can access
export const ROLE_ROUTES: Record<SupplierRoleType, string[]> = {
  Owner_Admin: [
    '/', '/product-catalog-grid-view', '/fifo-batch-inventory-manager',
    '/order-fulfillment-kanban-board', '/crm-retailer-profiles', '/price-lists',
    '/promotional-campaigns', '/sales-reps', '/delivery-scheduling', '/fleet-management',
    '/delivery-confirmation', '/returns-management', '/debt-collection',
    '/profitability-reports', '/cheque-settlement', '/purchase-orders',
    '/supplier-roles', '/kpi-dashboard', '/tva-report', '/whatsapp-notifications',
  ],
  Sales_Manager: [
    '/', '/crm-retailer-profiles', '/price-lists', '/promotional-campaigns',
    '/sales-reps', '/profitability-reports', '/kpi-dashboard',
  ],
  Sales_Rep: [
    '/', '/crm-retailer-profiles', '/direct-order-sheet',
  ],
  Warehouse_Manager: [
    '/', '/fifo-batch-inventory-manager', '/returns-management', '/purchase-orders',
    '/product-catalog-grid-view',
  ],
  Driver: [
    '/', '/delivery-scheduling', '/delivery-confirmation', '/fleet-management',
  ],
  Accountant: [
    '/', '/debt-collection', '/cheque-settlement', '/tva-report',
    '/kpi-dashboard', '/profitability-reports',
  ],
};

export function RoleProvider({ children }: { children: React.ReactNode }) {
  const [activeStaff, setActiveStaff] = useState<StaffMember | null>(null);

  const loginWithPin = useCallback(async (pin: string, supplierId?: number): Promise<boolean> => {
    const supabase = createClient();
    try {
      let query = supabase
        .from('supplier_staff_roles')
        .select('*')
        .eq('pin_code', pin)
        .eq('is_active', true);

      if (supplierId) {
        query = query.eq('supplier_id', supplierId);
      }

      const { data, error } = await query.limit(1).maybeSingle();

      if (error || !data) {
        toast.error('كود PIN غير صحيح — Code PIN incorrect');
        return false;
      }

      const staff: StaffMember = {
        roleId: data.role_id,
        supplierId: data.supplier_id,
        staffName: data.staff_name,
        roleType: data.role_type as SupplierRoleType,
        pinCode: data.pin_code,
        phoneWhatsapp: data.phone_whatsapp,
        isActive: data.is_active,
        permissions: data.permissions || {},
      };

      setActiveStaff(staff);
      toast.success(`مرحباً ${staff.staffName} — Bienvenue ${staff.staffName}`);
      return true;
    } catch {
      toast.error('خطأ في الاتصال — Erreur de connexion');
      return false;
    }
  }, []);

  const logoutRole = useCallback(() => {
    setActiveStaff(null);
    toast.success('تم تسجيل الخروج — Déconnecté');
  }, []);

  const hasPermission = useCallback((key: string): boolean => {
    if (!activeStaff) return false;
    if (activeStaff.permissions?.all === true) return true;
    return activeStaff.permissions?.[key] === true;
  }, [activeStaff]);

  const getRoleLabel = useCallback((role: SupplierRoleType): string => {
    return ROLE_LABELS[role]?.ar || role;
  }, []);

  const getRoleColor = useCallback((role: SupplierRoleType): string => {
    return ROLE_COLORS[role] || 'bg-gray-100 text-gray-700';
  }, []);

  const getAllowedRoutes = useCallback((role: SupplierRoleType): string[] => {
    return ROLE_ROUTES[role] || [];
  }, []);

  return (
    <RoleContext.Provider value={{
      activeStaff,
      isRoleAuthenticated: !!activeStaff,
      loginWithPin,
      logoutRole,
      hasPermission,
      getRoleLabel,
      getRoleColor,
      getAllowedRoutes,
    }}>
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  return useContext(RoleContext);
}
